import { PatientFlag } from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import { createReferenceStringWithHistory } from '@seen/core/references'
import { groupBy, orderBy } from 'lodash-es'
import type { ReactNode } from 'react'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import {
  formatOosaHoverDate,
  formatOosaPillDateRange,
} from '#src/components/oosa/oosaFlag.ts'
import { useBulkResourceVersions } from '#src/hooks/useResourceVersions.ts'

export function useOosaFlagActivity(
  flagIds: readonly string[] | undefined,
  fallbackFlags: readonly PatientFlag[] = []
): ReactNode[] {
  const uniqueIds = [...new Set(flagIds ?? [])].filter(Boolean)
  const { data: historyVersions = [] } = useBulkResourceVersions(
    PatientFlag,
    uniqueIds
  )

  const historyById = groupBy(
    historyVersions,
    (version) => version.toFhirResource().id ?? ''
  )
  const fallbackById = groupBy(
    fallbackFlags,
    (flag) => flag.toFhirResource().id ?? ''
  )
  const described = [...new Set([...uniqueIds, ...Object.keys(fallbackById)])]
    .filter(Boolean)
    .flatMap((flagId) => {
      const fallback = fallbackById[flagId]?.[0]
      return describeOosaFlagVersions(
        versionsForOosaHistory(historyById[flagId], fallback)
      )
    })

  return orderBy(
    described,
    [(entry) => versionTime(entry.version)],
    ['desc']
  ).map(({ version, previous, action }) => {
    const fhir = version.toFhirResource()
    return (
      <ActivityEntry
        key={
          fhir.meta?.versionId
            ? createReferenceStringWithHistory(fhir)
            : (fhir.id ?? action)
        }
        author={version.resourceAuthor}
        time={versionTime(version) || undefined}
        action={action}
      >
        {version.oosaNotes && version.oosaNotes !== previous?.oosaNotes && (
          <div className="rounded-md bg-muted px-2 py-1 text-xs whitespace-pre-wrap text-foreground-secondary">
            {version.oosaNotes}
          </div>
        )}
      </ActivityEntry>
    )
  })
}

export function versionsForOosaHistory(
  history: readonly PatientFlag[] | undefined,
  fallback: PatientFlag | undefined
): PatientFlag[] {
  if (!fallback) {
    return history ? [...history] : []
  }
  if (!history || history.length === 0) {
    return [fallback]
  }
  const fallbackVersionId = fallback.toFhirResource().meta?.versionId
  if (
    fallbackVersionId &&
    history.some(
      (version) =>
        version.toFhirResource().meta?.versionId === fallbackVersionId
    )
  ) {
    return [...history]
  }
  return [...history, fallback]
}

interface DescribedOosaFlagVersion {
  version: PatientFlag
  previous: PatientFlag | undefined
  action: string
}

function describeOosaFlagVersions(
  versions: readonly PatientFlag[]
): DescribedOosaFlagVersion[] {
  const ascending = orderBy(
    versions,
    [(version) => versionTime(version)],
    ['asc']
  )
  const entries: DescribedOosaFlagVersion[] = []
  ascending.forEach((version, index) => {
    const previous = index > 0 ? ascending[index - 1] : undefined
    const action = describeOosaFlagChange(previous, version)
    if (!action) {
      return
    }
    entries.push({ version, previous, action })
  })
  return entries
}

export function describeOosaFlagChange(
  previous: PatientFlag | undefined,
  current: PatientFlag
): string | undefined {
  const range = oosaDateRangeLabel(current)

  if (!previous) {
    return range ? `created the OOSA flag (${range})` : 'created the OOSA flag'
  }

  if (previous.status !== current.status) {
    if (current.status === 'inactive') {
      return 'inactivated the OOSA flag'
    }
    if (current.status === 'active') {
      return 'reactivated the OOSA flag'
    }
  }

  if (previous.start !== current.start || previous.end !== current.end) {
    if (previous.start !== current.start && previous.end === current.end) {
      return current.start
        ? `updated the start date to ${formatOosaHoverDate(current.start)}`
        : 'cleared the start date'
    }
    if (previous.end !== current.end && previous.start === current.start) {
      return current.end
        ? `updated the return date to ${formatOosaHoverDate(current.end)}`
        : 'cleared the return date'
    }
    return range ? `updated the dates to ${range}` : 'updated the dates'
  }

  if (previous.reason !== current.reason) {
    const reason =
      current.oosaDetails != null
        ? patients.formatOosaReasonDisplay(
            current.oosaDetails.reason,
            current.oosaDetails.otherReason
          )
        : current.reason
    return reason ? `updated the reason to ${reason}` : 'cleared the reason'
  }

  if (previous.oosaNotes !== current.oosaNotes) {
    return current.oosaNotes ? 'added a note' : 'cleared the note'
  }

  return undefined
}

function oosaDateRangeLabel(flag: PatientFlag): string | undefined {
  if (!flag.start || !flag.end) {
    return undefined
  }
  return formatOosaPillDateRange(flag.start, flag.end)
}

function versionTime(flag: PatientFlag): string {
  return flag.toFhirResource().meta?.lastUpdated ?? ''
}
